import React, { useMemo } from 'react';
import { Camera, Calendar, Star } from 'lucide-react';
import { FamilyMember } from '../types';

interface QuestBoardProps {
  members: Record<string, FamilyMember>;
  onSolve: (type: 'photo' | 'info', memberId: string) => void;
  currentUser?: string;
}

const QuestBoard: React.FC<QuestBoardProps> = ({ members, onSolve, currentUser }) => {
  
  // --- QUEST GENERATOR ALGORITHM ---
  const quests = useMemo(() => {
    const list: any[] = [];
    const memberArray = Object.values(members);

    // 1. Find missing photos (High Priority)
    const missingPhotos = memberArray.filter(m => !m.avatarUrl || m.avatarUrl.includes('ui-avatars'));
    if (missingPhotos.length > 0) {
      // Pick a random one to keep it fresh
      const target = missingPhotos[Math.floor(Math.random() * missingPhotos.length)];
      list.push({
        id: 'q-photo',
        type: 'photo',
        title: 'Missing Face',
        desc: `We don't have a photo of ${target.name} yet.`,
        xp: 150,
        targetId: target.id,
        icon: Camera,
        color: 'bg-blue-500'
      });
    }

    // 2. Find missing birthdays (Medium Priority)
    const missingInfo = memberArray.filter(m => !m.gender || m.gender === 'Unknown'); 
    if (missingInfo.length > 0) {
      const target = missingInfo[Math.floor(Math.random() * missingInfo.length)];
      list.push({
        id: 'q-info',
        type: 'info',
        title: 'Unknown Detail',
        desc: `Update gender/info for ${target.name}.`,
        xp: 50,
        targetId: target.id,
        icon: Calendar,
        color: 'bg-purple-500'
      });
    }

    // 3. "Connector" Quest (Always available)
    list.push({
      id: 'q-invite',
      type: 'invite',
      title: 'Grow the Tree',
      desc: 'Invite a cousin to join the app.',
      xp: 200,
      targetId: 'invite',
      icon: Star,
      color: 'bg-amber-500'
    });

    return list;
  }, [members]);

  // --- HANDLER: Click Logic ---
  const handleQuestClick = (quest: any) => {
    if (quest.type === 'invite') {
      // 1. Get familyId from current user
      const inviterMember = currentUser ? members[currentUser] : Object.values(members)[0];
      const familyId = inviterMember?.familyId;
      
      // 2. Create invite URL with familyId parameter
      const inviteUrl = familyId 
        ? `https://famitry.in?familyId=${encodeURIComponent(familyId)}`
        : "https://famitry.in";
      
      // 3. Custom WhatsApp Logic
      const inviteText = `Hey! I'm building our family tree on Famitry. Join me here to see photos and add your history: ${inviteUrl}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
      
      // 4. Open in new tab (works on mobile and desktop)
      window.open(whatsappUrl, '_blank');
    } else {
      // 2. Default logic for other quests (Photo/Info)
      onSolve(quest.type, quest.targetId);
    }
  };

  return (
    <div className="mb-8 overflow-hidden">
      <div className="flex items-center justify-between mb-4 px-2">
        <h3 className="font-black text-gray-900 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500 fill-current" /> Daily Quests
        </h3>
        <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
          {quests.length} Active
        </span>
      </div>

      {/* Horizontal Scroll Container */}
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4 px-2">
        {quests.map((quest) => (
          <div 
            key={quest.id} 
            className="min-w-[260px] bg-white rounded-2xl p-4 shadow-sm border border-gray-100 relative group hover:shadow-md transition-all cursor-pointer"
            onClick={() => handleQuestClick(quest)}
          >
            <div className={`absolute top-4 right-4 w-8 h-8 rounded-full ${quest.color} flex items-center justify-center text-white shadow-lg`}>
              <quest.icon className="w-4 h-4" />
            </div>
            
            <div className="mt-8">
              <h4 className="font-bold text-gray-900">{quest.title}</h4>
              <p className="text-xs text-gray-500 mt-1 mb-3">{quest.desc}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-amber-500 flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> +{quest.xp} XP
                </span>
              </div>
            </div>
            
            {/* Progress Bar Visual */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 rounded-b-2xl overflow-hidden">
               <div className={`h-full w-0 group-hover:w-full transition-all duration-1000 ${quest.color}`}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestBoard;